<script setup>
  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import {assignmentRole, doctorProfession} from "@pages/Contact/Enums/Enums.js";
  import {MedIcon, MedLink} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({name: "ComponentContactCard"});

  defineProps({
    contact: {
      type: Object,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
  });
</script>

<template>
  <div
    class="border-t-2 border-t-gray-50 p-2 shadow-md"
    :class="{'min-h-40': !small}"
    :data-test="getTestId('contact-card')"
  >
    <div class="text-sm text-gray-500">
      {{ doctorProfession[contact.profession] ?? "&nbsp;" }}
    </div>

    <h6 class="flex items-start justify-between">
      <span
        v-if="contact.firstname || contact.lastname"
        class="font-semibold text-gray-500"
        :class="{'text-xl': !small, 'text-lg': small}"
      >
        {{ contact.title }} {{ contact.firstname }} {{ contact.lastname }}
      </span>

      <span v-else class="font-semibold text-gray-500" :class="{'text-xl': !small, 'text-lg': small}">
        {{ contact.company }}
      </span>

      <ComponentCategoryBadge v-if="contact.role" class="mt-1" color="red">
        {{ assignmentRole[contact.role] }}
      </ComponentCategoryBadge>
    </h6>

    <address
      v-if="contact.company || contact.street || contact.zip || contact.city"
      class="mt-2 not-italic"
      :class="{'text-sm': small}"
    >
      <!-- without firstname and lastname the company is already shown above! -->
      <div v-if="(contact.firstname || contact.lastname) && contact.company">{{ contact.company }}</div>
      <div v-if="contact.street">{{ contact.street }}</div>
      <div v-if="contact.zip || contact.city">{{ contact.zip }} {{ contact.city }}</div>
    </address>

    <div
      v-if="contact.email || contact.telephone || contact.fax"
      :class="cn('mt-2 flex flex-col gap-1', {'text-sm': small})"
    >
      <template v-if="contact.email">
        <MedLink
          variant="primary"
          icon="email"
          size="sm"
          :href="`mailto:${contact.email}`"
          :data-test="getTestId('contact-card-mailto-link')"
        >
          {{ contact.email }}
        </MedLink>
      </template>

      <template v-if="contact.telephone">
        <MedLink
          variant="primary"
          icon="phone"
          size="sm"
          :href="`tel:${contact.telephone}`"
          :data-test="getTestId('contact-card-telephone-link')"
        >
          {{ contact.telephone }}
        </MedLink>
      </template>

      <div v-if="contact.fax" class="flex items-center gap-1">
        <MedIcon icon="print" size="sm" class="text-gray-500" />
        <span class="">{{ contact.fax }}</span>
      </div>
    </div>
  </div>
</template>
